<template>
    <Modal
        :model-value="modelValue"
        width="95vw"
        height="95vh"
        class-name="add-templates-modal"
        @onCancel="$emit('update:modelValue', false)"
    >
        <template #header>
            <h3>{{ campaignType ? TemplateTypeLabel[campaignType] + " " : "" }}Master Templates</h3>
        </template>

        <div v-if="masterTemplatesToAdd.length" class="templates-modal__sidebar">
            <MasterTemplateSizeFilter
                class="templates-modal__sidebar-filter"
                :campaign-master-template-ids="masterTemplateIds"
            />

            <MasterTemplateTypeFilter class="templates-modal__sidebar-filter" :is-used-in-campaign="!!campaignType" />
            <MasterTemplateResizedFilter class="templates-modal__sidebar-filter" />
            <MasterTemplateFamilyFilter class="templates-modal__sidebar-filter" :is-used-in-campaign="!!campaignType" />
        </div>

        <div class="templates-modal__body">
            <div class="templates-modal__body-content">
                <MasterTemplateLibrary
                    :campaign-master-template-ids="masterTemplateIds"
                    in-modal
                    class="templates-modal__library"
                    @select="onTemplateSelect"
                    @add="addNewTemplates"
                />
            </div>
        </div>

        <template #footer>
            <Button type="text" size="large" @click="cancelTemplateSelection">Cancel</Button>
            <Button
                type="primary"
                size="large"
                data-testid="master-templates__button-add"
                :disabled="!selectedTemplatesIds.length"
                @click="addNewTemplates"
            >
                Add to campaign
            </Button>
        </template>
    </Modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapStores } from "pinia";

import MasterTemplateSizeFilter from "@/components/Campaign/MasterTemplates/MasterTemplateSizeFilter.vue";
import MasterTemplateResizedFilter from "@/components/Campaign/MasterTemplates/MasterTemplateResizedFilter.vue";
import MasterTemplateFamilyFilter from "@/components/Campaign/MasterTemplates/MasterTemplateFamilyFilter.vue";
import MasterTemplateLibrary from "@/components/Campaign/MasterTemplateLibrary.vue";
import bus from "@/bus";
import { EditorEvents } from "@/enums/editor";
import MasterTemplateTypeFilter from "./MasterTemplates/MasterTemplateTypeFilter.vue";
import { TemplateTypeLabel } from "@/enums/masterTemplate";
import { useMasterTemplatesLibraryStore } from "@/store/pinia/masterTemplatesLibrary";

export default defineComponent({
    name: "CampaignAddTemplatesModal",
    components: {
        MasterTemplateSizeFilter,
        MasterTemplateResizedFilter,
        MasterTemplateFamilyFilter,
        MasterTemplateLibrary,
        MasterTemplateTypeFilter
    },

    props: {
        modelValue: {
            type: Boolean,
            required: true
        }
    },

    emits: ["update:modelValue"],

    data() {
        return {
            TemplateTypeLabel,
            selectedTemplatesIds: [] as string[]
        };
    },

    computed: {
        ...mapStores(useMasterTemplatesLibraryStore),
        campaignType() {
            return this.$store.state.campaign.campaignType!;
        },

        masterTemplateIds() {
            return this.$store.state.campaign.normalized.masterTemplateIds;
        },

        masterTemplatesToAdd() {
            return this.masterTemplatesLibraryStore.masterTemplateIds.filter(
                templateId => !this.masterTemplateIds.includes(templateId)
            );
        }
    },

    created() {
        this.masterTemplatesLibraryStore.types = this.campaignType ? [this.campaignType] : [];
    },

    unmounted() {
        this.masterTemplatesLibraryStore.types = [];
    },

    methods: {
        onTemplateSelect(selectedTemplatesIds: string[]) {
            this.selectedTemplatesIds = selectedTemplatesIds;
        },

        cancelTemplateSelection() {
            this.selectedTemplatesIds = [];
            this.$emit("update:modelValue", false);
        },

        async addNewTemplates() {
            if (!this.selectedTemplatesIds) {
                this.$emit("update:modelValue", false);
                return;
            }

            const templatesIds = this.selectedTemplatesIds.filter(id => !this.masterTemplateIds.includes(id));

            bus.$emit(EditorEvents.AddTemplatesToCampaign, templatesIds);
            this.$emit("update:modelValue", false);
        }
    }
});
</script>
